import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router'

type DefineSubModule = {
  code: string
  label: string
  order: number
  enabled?: boolean
  subModules?: DefineSubModule[]
}

type UseDefineDataTypeResult = {
  /** The dataType code from navigation API (e.g. 'SECTION_KEYWORD', 'PAYEE_MASTER') */
  dataType: string | null
  /** Lowercase version for API paths (e.g. 'section-keyword', 'payee_master') */
  dataTypeLower: string | null
  /** Label from navigation API (e.g. 'Service Keyword', 'Payee Master') */
  label: string | null
  /** Child sub-modules if present (e.g. SERVICE_MASTER has NATURE_OF_SERVICE & DTAA_MASTER) */
  subModules: DefineSubModule[]
  /** Whether nav data has loaded */
  isLoaded: boolean
}

/**
 * Hook that extracts the dataType for the current Define sub-module from the navigation API.
 *
 * Instead of hardcoding DATA_TYPE constants, components use this hook to derive
 * the dataType dynamically from the navigation API response stored in Redux.
 *
 * The hook matches the last segment of the current route path against the
 * `code` field of sub-modules under RETURNS > define in the navigation data.
 *
 * @param fallbackCode - Optional fallback code if navigation data hasn't loaded yet
 * @returns The dataType info from navigation API
 *
 * @example
 * // In MTServiceKeywordTable (route: /mt/returns/define/SECTION_KEYWORD)
 * const { dataType, dataTypeLower } = useDefineDataType('SECTION_KEYWORD')
 * // dataType = 'SECTION_KEYWORD', dataTypeLower = 'section-keyword'
 *
 * @example
 * // In MTServiceMasterTable (route: /mt/returns/define/SERVICE_MASTER)
 * const { dataType, subModules } = useDefineDataType('SERVICE_MASTER')
 * // dataType = 'SERVICE_MASTER', subModules = [{ code: 'NATURE_OF_SERVICE', ... }, { code: 'DTAA_MASTER', ... }]
 */
export function useDefineDataType(
  fallbackCode?: string
): UseDefineDataTypeResult {
  const { navModules } = useSelector((state: any) => state.navigation)
  const location = useLocation()

  return useMemo(() => {
    // Extract the route segment after /define/ (e.g. 'SECTION_KEYWORD' from '/mt/returns/define/SECTION_KEYWORD')
    const pathSegments = location.pathname.split('/')
    const defineIdx = pathSegments.indexOf('define')
    const routeCode =
      defineIdx >= 0 && defineIdx < pathSegments.length - 1
        ? pathSegments[defineIdx + 1]
        : null

    if (!navModules) {
      // Navigation data not yet loaded - use fallback
      const code = routeCode || fallbackCode || null
      return {
        dataType: code,
        dataTypeLower: code ? code.toLowerCase().replace(/_/g, '-') : null,
        label: null,
        subModules: [],
        isLoaded: false,
      }
    }

    // Find RETURNS module
    const returnsModule = navModules.find((mod: any) => mod.code === 'RETURNS')
    if (!returnsModule?.subModules) {
      const code = routeCode || fallbackCode || null
      return {
        dataType: code,
        dataTypeLower: code ? code.toLowerCase().replace(/_/g, '-') : null,
        label: null,
        subModules: [],
        isLoaded: true,
      }
    }

    // Find the 'define' sub-module
    const defineModule = returnsModule.subModules.find(
      (sub: any) => sub.code === 'define' || sub.code === 'DEFINE'
    )
    if (!defineModule?.subModules) {
      const code = routeCode || fallbackCode || null
      return {
        dataType: code,
        dataTypeLower: code ? code.toLowerCase().replace(/_/g, '-') : null,
        label: null,
        subModules: [],
        isLoaded: true,
      }
    }

    // Match current route segment against define's sub-modules
    const matchingSubModule = defineModule.subModules.find(
      (sub: DefineSubModule) => sub.code === routeCode
    )

    if (matchingSubModule) {
      const code = matchingSubModule.code
      return {
        dataType: code,
        dataTypeLower: code.toLowerCase().replace(/_/g, '-'),
        label: matchingSubModule.label,
        subModules: matchingSubModule.subModules || [],
        isLoaded: true,
      }
    }

    // No match found - use fallback
    const code = routeCode || fallbackCode || null
    return {
      dataType: code,
      dataTypeLower: code ? code.toLowerCase().replace(/_/g, '-') : null,
      label: null,
      subModules: [],
      isLoaded: true,
    }
  }, [navModules, location.pathname, fallbackCode])
}
